/**
 * Das Speicher-Tor fuer Auswahlwechsel AUSSERHALB der Werkbank.
 *
 * K1-Befund (Linsen L3 + L6): der V2-Original-Befund nannte den FEEDWECHSEL
 * ausdruecklich als einen der kaputten Bewegungspfade — er ist zwischen
 * Befund und Fix (#158) verlorengegangen. Das Tor `withSavedEdits` lebt im
 * WorkbenchReviewer und kann den globalen FeedSwitcher (Layout) nicht
 * erreichen; ein Feedwechsel mit offener Handkorrektur speicherte deshalb
 * weiter fire-and-forget, und bei einem Fehlschlag war der Text weg,
 * waehrend der Nutzer schon den anderen Feed ansah.
 *
 * Muster wie `useStickyActionBar`: ein Modul-Register verbindet die beiden
 * Baumhaelften ohne neuen Context. Die Werkbank registriert ihren
 * Speicher-Kern (`flushEdits`), der FeedSwitcher fragt VOR jedem Wechsel
 * nach (`guardedSelectionChange`) — und wechselt bei Fehlschlag NICHT.
 * Ausserhalb der Werkbank ist kein Waechter registriert und jeder Wechsel
 * laeuft ungebremst.
 */

/** true = gespeichert oder nichts zu speichern; false = Fehlschlag, NICHT wechseln. */
type PendingEditsGuard = () => Promise<boolean>;

let activeGuard: PendingEditsGuard | null = null;

/**
 * Registriert den Waechter der aktuell montierten Werkbank. Gibt die
 * Abmeldung zurueck; sie entfernt nur den EIGENEN Waechter (StrictMode
 * doppelt Effekte — eine pauschale Abmeldung wuerde den Nachfolger treffen).
 */
export function registerPendingEditsGuard(guard: PendingEditsGuard): () => void {
  activeGuard = guard;
  return () => {
    if (activeGuard === guard) activeGuard = null;
  };
}

/**
 * Vor einem Auswahlwechsel rufen. Wirft nie — ein werfender Waechter zaehlt
 * als Fehlschlag, denn die einzig sichere Antwort auf "Speichern kaputt"
 * ist Stehenbleiben (dasselbe Muster wie im Tor der Werkbank).
 */
export async function flushPendingEditsBeforeSelectionChange(): Promise<boolean> {
  if (!activeGuard) return true;
  try {
    return await activeGuard();
  } catch {
    return false;
  }
}

/** Nur fuer Tests: Register leeren, damit Suiten sich nicht gegenseitig sehen. */
export function resetPendingEditsGuardForTests(): void {
  activeGuard = null;
}

/**
 * Wechsel mit Speicher-Tor UND Rueckmeldung (Eine-Zeile-Feed-Switcher).
 * Bewusst NICHT fire-and-forget: der Aufrufer (FeedSwitcher-Popover) muss
 * wissen, ob gewechselt wurde, um erst DANACH zu schliessen. true = Guard
 * gruen und apply gelaufen; false = Guard rot, apply nie gerufen. Fehler aus
 * apply propagieren (Aufrufer zeigt sie an).
 */
export async function guardedSelectionChange(apply: () => Promise<void>): Promise<boolean> {
  if (!(await flushPendingEditsBeforeSelectionChange())) return false;
  await apply();
  return true;
}
